<template>
    <LoadingComponent :props="loading" />

    <div id="system" class="db-card db-tab-div active" :class="{ 'on-update': onUpdateActive }">
        <div class="db-card-header flex items-center justify-between p-2 pl-4">
            <h3 class="db-card-title pt-1">{{ $t('label.system_and_maintenance') }}</h3>
            <div class="w-fit">
                <div class="inline-flex rounded-lg bg-gray-100 p-1 gap-1">
                    <button
                        type="button"
                        class="px-4 h-9 rounded-md text-sm font-medium transition"
                        :class="activeSystemTab === 'operations' ? 'bg-white shadow-sm text-primary' : 'text-gray-600 hover:text-primary'"
                        @click="setActiveSystemTab('operations')"
                    >
                        {{ $t('label.state') }} / {{ $t('label.commands') }}
                    </button>
                    <button
                        type="button"
                        class="px-4 h-9 rounded-md text-sm font-medium transition"
                        :class="activeSystemTab === 'configuration' ? 'bg-white shadow-sm text-primary' : 'text-gray-600 hover:text-primary'"
                        @click="setActiveSystemTab('configuration')"
                    >
                        {{ $t('label.settings') }}
                    </button>
                    <button
                        type="button"
                        class="px-4 h-9 rounded-md text-sm font-medium transition"
                        :class="activeSystemTab === 'backup' ? 'bg-white shadow-sm text-primary' : 'text-gray-600 hover:text-primary'"
                        @click="setActiveSystemTab('backup')"
                    >
                        {{ $t('label.backup_and_restore') }}
                    </button>
                </div>
            </div>
        </div>

        <div class="db-card-body">

            <form v-if="activeSystemTab === 'configuration'" @submit.prevent="save" class="w-full d-block">
                <div class="form-row">

                    <div class="form-col-12 border-b pb-2 mb-2">
                        <h4 class="font-client font-medium text-base">{{ $t('label.queue_and_jobs_configuration') }}</h4>
                    </div>

                    <div class="form-col-12 sm:form-col-6">
                        <label class="db-field-title required" for="queue_enable">{{ $t('label.queue_conversions') }}</label>
                        <div class="db-field-radio-group">
                            <div class="db-field-radio">
                                <div class="custom-radio">
                                    <input :value="1" v-model.number="form.system_queue_enabled" id="queue_enable" type="radio"
                                        class="custom-radio-field" />
                                    <span class="custom-radio-span"></span>
                                </div>
                                <label for="queue_enable" class="db-field-label">{{ $t('label.enable') }}</label>
                            </div>
                            <div class="db-field-radio">
                                <div class="custom-radio">
                                    <input :value="0" v-model.number="form.system_queue_enabled" id="queue_disable" type="radio"
                                        class="custom-radio-field" />
                                    <span class="custom-radio-span"></span>
                                </div>
                                <label for="queue_disable" class="db-field-label">{{ $t('label.queue_disable_immediate') }}</label>
                            </div>
                        </div>
                    </div>

                    <div class="form-col-12 sm:form-col-6">
                        <label for="queue_connection" class="db-field-title required">{{ $t('label.queue_connection') }}</label>
                        <vue-select class="db-field-control f-b-custom-select" id="queue_connection"
                            v-bind:class="errors.system_queue_connection ? 'is-invalid' : ''"
                            v-model="form.system_queue_connection" :options="queueConnectionOptions"
                            :closeOnSelect="true" :searchable="true" :clearOnClose="true" placeholder="--"
                            search-placeholder="--" />
                        <small class="db-field-alert" v-if="errors.system_queue_connection">{{ errors.system_queue_connection[0] }}</small>
                    </div>

                    <div class="form-col-12 border-b pb-2 mb-2 mt-2">
                        <h4 class="font-client font-medium text-base">{{ $t('label.environment_and_debug_mode') }}</h4>
                    </div>

                    <div class="form-col-12 sm:form-col-6">
                        <label class="db-field-title required" for="env_local">{{ $t('label.environment') }}</label>
                        <div class="db-field-radio-group">
                            <div class="db-field-radio">
                                <div class="custom-radio">
                                    <input value="local" v-model="form.system_app_env" id="env_local" type="radio"
                                        class="custom-radio-field" />
                                    <span class="custom-radio-span"></span>
                                </div>
                                <label for="env_local" class="db-field-label">{{ $t('label.local') }}</label>
                            </div>
                            <div class="db-field-radio">
                                <div class="custom-radio">
                                    <input value="production" v-model="form.system_app_env" id="env_production" type="radio"
                                        class="custom-radio-field" />
                                    <span class="custom-radio-span"></span>
                                </div>
                                <label for="env_production" class="db-field-label">{{ $t('label.production') }}</label>
                            </div>
                        </div>
                    </div>

                    <div class="form-col-12 sm:form-col-6">
                        <label class="db-field-title required" for="app_debug_enable">{{ $t('label.app_debug') }}</label>
                        <div class="db-field-radio-group">
                            <div class="db-field-radio">
                                <div class="custom-radio">
                                    <input :value="1" v-model.number="form.system_app_debug" id="app_debug_enable" type="radio"
                                        class="custom-radio-field" />
                                    <span class="custom-radio-span"></span>
                                </div>
                                <label for="app_debug_enable" class="db-field-label">{{ $t('label.enable') }}</label>
                            </div>
                            <div class="db-field-radio">
                                <div class="custom-radio">
                                    <input :value="0" v-model.number="form.system_app_debug" id="app_debug_disable" type="radio"
                                        class="custom-radio-field" />
                                    <span class="custom-radio-span"></span>
                                </div>
                                <label for="app_debug_disable" class="db-field-label">{{ $t('label.disable') }}</label>
                            </div>
                        </div>
                    </div>

                    <div class="form-col-12 border-b pb-2 mb-2 mt-2">
                        <h4 class="font-client font-medium text-base">{{ $t('label.login_and_registration_mode') }}</h4>
                    </div>

                    <!-- 1. MODO DEMO -->
                    <div class="form-col-12 sm:form-col-6">
                        <label class="db-field-title required" for="demo_enable">{{ $t('label.demo_mode') }}</label>
                        <div class="db-field-radio-group">
                            <div class="db-field-radio">
                                <div class="custom-radio">
                                    <input :value="1" v-model.number="form.system_demo" id="demo_enable" type="radio"
                                        class="custom-radio-field" />
                                    <span class="custom-radio-span"></span>
                                </div>
                                <label for="demo_enable" class="db-field-label">{{ $t('label.enable') }}</label>
                            </div>
                            <div class="db-field-radio">
                                <div class="custom-radio">
                                    <input :value="0" v-model.number="form.system_demo" id="demo_disable" type="radio"
                                        class="custom-radio-field" />
                                    <span class="custom-radio-span"></span>
                                </div>
                                <label for="demo_disable" class="db-field-label">{{ $t('label.disable') }}</label>
                            </div>
                        </div>
                    </div>

                    <!-- 2. HABILITAR LOGIN AUTOMÁTICO (Dependiente de Modo Demo) -->
                    <div class="form-col-12 sm:form-col-6">
                        <label class="db-field-title required" for="auto_login_enable">
                            {{ $t('label.auto_login_enable') }}
                            <small v-if="form.system_demo === 0" class="lowercase text-xs text-amber-600 font-normal mt-0.5">{{ $t('label.requires_demo_mode') }}</small>
                        </label>
                        <div class="db-field-radio-group" :class="{ 'opacity-50 pointer-events-none': form.system_demo === 0 }">
                            <div class="db-field-radio">
                                <div class="custom-radio">
                                    <input :value="1" v-model.number="form.system_auto_login" :disabled="form.system_demo === 0" id="auto_login_enable" type="radio"
                                        class="custom-radio-field" />
                                    <span class="custom-radio-span"></span>
                                </div>
                                <label for="auto_login_enable" class="db-field-label">{{ $t('label.enable') }}</label>
                            </div>
                            <div class="db-field-radio">
                                <div class="custom-radio">
                                    <input :value="0" v-model.number="form.system_auto_login" :disabled="form.system_demo === 0" id="auto_login_disable" type="radio"
                                        class="custom-radio-field" />
                                    <span class="custom-radio-span"></span>
                                </div>
                                <label for="auto_login_disable" class="db-field-label">{{ $t('label.disable') }}</label>
                            </div>
                        </div>
                    </div>

                    <!-- 3. MODO DE INICIO DE SESIÓN (Dependiente de Login Automático) -->
                    <div class="form-col-12 sm:form-col-6">
                        <label class="db-field-title required" for="login_mode_auto">
                            {{ $t('label.login_mode') }}
                            <small v-if="form.system_demo === 0 || form.system_auto_login === 0" class="lowercase text-xs text-amber-600 font-normal mt-0.5">{{ $t('label.requires_auto_login_mode') }}</small>
                        </label>
                        <div class="db-field-radio-group" :class="{ 'opacity-50 pointer-events-none': form.system_demo === 0 || form.system_auto_login === 0 }">
                            <div class="db-field-radio">
                                <div class="custom-radio">
                                    <input value="auto" v-model="form.system_login_mode" :disabled="form.system_demo === 0 || form.system_auto_login === 0" id="login_mode_auto" type="radio"
                                        class="custom-radio-field" />
                                    <span class="custom-radio-span"></span>
                                </div>
                                <label for="login_mode_auto" class="db-field-label">{{ $t('label.login_mode_auto') }}</label>
                            </div>
                            <div class="db-field-radio">
                                <div class="custom-radio">
                                    <input value="normal" v-model="form.system_login_mode" :disabled="form.system_demo === 0 || form.system_auto_login === 0" id="login_mode_normal" type="radio"
                                        class="custom-radio-field" />
                                    <span class="custom-radio-span"></span>
                                </div>
                                <label for="login_mode_normal" class="db-field-label">{{ $t('label.login_mode_normal') }}</label>
                            </div>
                        </div>
                    </div>

                    <!-- 4. MODAL SORTEO LEAD CAPTURE (Dependiente de Login Automático) -->
                    <div class="form-col-12 sm:form-col-6">
                        <label class="db-field-title required" for="demo_giveaway_enable">
                            {{ $t('label.demo_giveaway_enable') }}
                            <small v-if="form.system_demo === 0 || form.system_auto_login === 0" class="lowercase text-xs text-amber-600 font-normal mt-0.5">{{ $t('label.requires_auto_login_mode') }}</small>
                        </label>
                        <div class="db-field-radio-group" :class="{ 'opacity-50 pointer-events-none': form.system_demo === 0 || form.system_auto_login === 0 }">
                            <div class="db-field-radio">
                                <div class="custom-radio">
                                    <input :value="1" v-model.number="form.system_demo_giveaway" :disabled="form.system_demo === 0 || form.system_auto_login === 0" id="demo_giveaway_enable" type="radio"
                                        class="custom-radio-field" />
                                    <span class="custom-radio-span"></span>
                                </div>
                                <label for="demo_giveaway_enable" class="db-field-label">{{ $t('label.enable') }}</label>
                            </div>
                            <div class="db-field-radio">
                                <div class="custom-radio">
                                    <input :value="0" v-model.number="form.system_demo_giveaway" :disabled="form.system_demo === 0 || form.system_auto_login === 0" id="demo_giveaway_disable" type="radio"
                                        class="custom-radio-field" />
                                    <span class="custom-radio-span"></span>
                                </div>
                                <label for="demo_giveaway_disable" class="db-field-label">{{ $t('label.disable') }}</label>
                            </div>
                        </div>
                    </div>

                    <div class="form-col-12 border-b pb-2 mb-2 mt-2">
                        <h4 class="font-client font-medium text-base">{{ $t('label.database_configuration') }}</h4>
                    </div>

                    <div class="form-col-12 sm:form-col-6">
                        <label for="db_connection" class="db-field-title required">{{ $t('label.db_connection') }}</label>
                        <vue-select class="db-field-control f-b-custom-select" id="db_connection"
                            v-bind:class="errors.system_db_connection ? 'is-invalid' : ''"
                            v-model="form.system_db_connection" :options="dbConnectionOptions"
                            :closeOnSelect="true" :searchable="true" :clearOnClose="true" placeholder="--"
                            search-placeholder="--" />
                        <small class="db-field-alert" v-if="errors.system_db_connection">{{ errors.system_db_connection[0] }}</small>
                    </div>

                    <div v-if="showDbNetworkFields" class="form-col-12 sm:form-col-6">
                        <label for="db_host" class="db-field-title required">{{ $t('label.db_host') }}</label>
                        <input v-model="form.system_db_host" v-bind:class="errors.system_db_host ? 'invalid' : ''" type="text"
                            id="db_host" class="db-field-control" />
                        <small class="db-field-alert" v-if="errors.system_db_host">{{ errors.system_db_host[0] }}</small>
                    </div>

                    <div v-if="showDbNetworkFields" class="form-col-12 sm:form-col-6">
                        <label for="db_port" class="db-field-title required">{{ $t('label.db_port') }}</label>
                        <input v-model="form.system_db_port" v-bind:class="errors.system_db_port ? 'invalid' : ''" type="text"
                            id="db_port" class="db-field-control" />
                        <small class="db-field-alert" v-if="errors.system_db_port">{{ errors.system_db_port[0] }}</small>
                    </div>

                    <div class="form-col-12 sm:form-col-6">
                        <label for="db_database" class="db-field-title required">{{ $t('label.db_database') }}</label>
                        <input v-model="form.system_db_database" v-bind:class="errors.system_db_database ? 'invalid' : ''" type="text"
                            id="db_database" class="db-field-control" />
                        <small class="db-field-alert" v-if="errors.system_db_database">{{ errors.system_db_database[0] }}</small>
                    </div>

                    <div v-if="showDbAuthFields" class="form-col-12 sm:form-col-6">
                        <label for="db_username" class="db-field-title required">{{ $t('label.db_username') }}</label>
                        <input v-model="form.system_db_username" v-bind:class="errors.system_db_username ? 'invalid' : ''" type="text"
                            id="db_username" class="db-field-control" />
                        <small class="db-field-alert" v-if="errors.system_db_username">{{ errors.system_db_username[0] }}</small>
                    </div>

                    <div v-if="showDbAuthFields" class="form-col-12 sm:form-col-6">
                        <label for="db_password" class="db-field-title">{{ $t('label.db_password') }}</label>
                        <input v-model="form.system_db_password" v-bind:class="errors.system_db_password ? 'invalid' : ''" type="password"
                            id="db_password" class="db-field-control" autocomplete="new-password" />
                        <small class="db-field-alert" v-if="errors.system_db_password">{{ errors.system_db_password[0] }}</small>
                    </div>

                    <div class="form-col-12 border-t pt-5 mt-3">
                        <button type="submit" class="db-btn text-white bg-primary hover:opacity-80" :class="{ 'disabled:opacity-50 disabled:bg-gray': isSaving || isAnyCommandRunning || isResettingBrowserCache }" :disabled="isSaving || isAnyCommandRunning || isResettingBrowserCache">
                            <template v-if="isSaving">
                                <svg class="inline-block mr-1 animate-spin" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                                    <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="3" opacity="0.25"></circle>
                                    <path d="M22 12a10 10 0 0 1-10 10" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"></path>
                                </svg>
                                <span>{{ $t('button.updating') }}</span>
                            </template>
                            <template v-else>
                                <i class="fa-solid fa-floppy-disk"></i>
                                <span>{{ $t('button.save_settings') }}</span>
                            </template>
                        </button>
                    </div>
                </div>
            </form>

            <!-- TAB DE RESPALDOS Y MIGRACIÓN -->
            <div v-else-if="activeSystemTab === 'backup'" class="w-full d-block">
                <div class="form-row">
                    <!-- EXPORTAR / GENERAR RESPALDO -->
                    <div class="form-col-12 border-b pb-3 mb-3">
                        <h4 class="font-client font-medium text-base">{{ $t('label.backup_export_title') }}</h4>
                        <p class="text-xs text-gray-500 mt-1">{{ $t('label.backup_export_desc') }}</p>
                    </div>

                    <div class="form-col-12 mb-3">
                        <button
                            type="button"
                            @click="exportBackup"
                            :disabled="isExporting"
                            class="db-btn text-white bg-primary hover:opacity-80 flex items-center gap-2"
                        >
                            <template v-if="isExporting">
                                <svg class="inline-block mr-1 animate-spin" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                                    <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="3" opacity="0.25"></circle>
                                    <path d="M22 12a10 10 0 0 1-10 10" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"></path>
                                </svg>
                                <span>{{ $t('label.generating_backup') }}</span>
                            </template>
                            <template v-else>
                                <i class="fa-solid fa-box-archive"></i>
                                <span>{{ $t('label.generate_backup') }}</span>
                            </template>
                        </button>
                    </div>

                    <!-- IMPORTAR Y SOBREESCRIBIR RESPALDO -->
                    <div class="form-col-12 border-b pb-3 mb-3 mt-4">
                        <h4 class="font-client font-medium text-base">{{ $t('label.backup_import_title') }}</h4>
                        <p class="text-xs text-gray-500 mt-1">{{ $t('label.backup_import_desc') }}</p>
                    </div>

                    <div class="form-col-12">
                        <div class="p-3 rounded-lg bg-amber-100 border border-amber-500 text-amber-500 text-sm flex items-start gap-2">
                            <i class="fa-solid fa-triangle-exclamation text-amber-600 mt-0.5 flex-shrink-0"></i>
                            <span>{{ $t('label.backup_warning_notice') }}</span>
                        </div>
                    </div>

                    <!-- Switch de origen de importación -->
                    <div class="form-col-12">
                        <label class="db-field-title required">{{ $t('label.import_source') }}</label>
                        <div class="db-field-radio-group">
                            <div class="db-field-radio">
                                <div class="custom-radio">
                                    <input value="upload" v-model="importMode" id="import_upload" type="radio" class="custom-radio-field" />
                                    <span class="custom-radio-span"></span>
                                </div>
                                <label for="import_upload" class="db-field-label">{{ $t('label.import_source_upload') }}</label>
                            </div>
                            <div class="db-field-radio">
                                <div class="custom-radio">
                                    <input value="server" v-model="importMode" id="import_server" type="radio" class="custom-radio-field" />
                                    <span class="custom-radio-span"></span>
                                </div>
                                <label for="import_server" class="db-field-label">{{ $t('label.import_source_server') }}</label>
                            </div>
                        </div>
                    </div>

                    <!-- Selector de archivo local o del servidor -->
                    <div class="form-col-12">
                        <template v-if="importMode === 'upload'">
                            <label for="backup_file_upload" class="db-field-title required">{{ $t('label.select_file') }}</label>
                            <input
                                id="backup_file_upload"
                                type="file"
                                ref="backupFile"
                                accept=".zip"
                                @change="handleFileSelect"
                                class="db-field-control"
                            />
                        </template>

                        <template v-else>
                            <label for="backup_file_server" class="db-field-title required">{{ $t('label.select_server_backup') }}</label>
                            <vue-select
                                class="db-field-control f-b-custom-select"
                                id="backup_file_server"
                                v-model="selectedServerBackup"
                                :options="serverBackups"
                                label-by="filename"
                                value-by="filename"
                                :closeOnSelect="true"
                                :searchable="true"
                                :clearOnClose="true"
                                placeholder="--"
                                search-placeholder="--"
                            />
                        </template>
                    </div>

                    <div class="form-col-12">
                        <button
                            type="button"
                            @click="confirmImportBackup"
                            :disabled="isImporting || (importMode === 'upload' ? !selectedBackupFile : !selectedServerBackup)"
                            class="db-btn text-white bg-warning hover:opacity-80 flex items-center gap-2 disabled:opacity-50 disabled:bg-gray"
                        >
                            <template v-if="isImporting">
                                <svg class="inline-block mr-1 animate-spin" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                                    <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="3" opacity="0.25"></circle>
                                    <path d="M22 12a10 10 0 0 1-10 10" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"></path>
                                </svg>
                                <span>{{ $t('button.processing') }}</span>
                            </template>
                            <template v-else>
                                <i class="fa-solid fa-file-import"></i>
                                <span>{{ $t('button.start_import') }}</span>
                            </template>
                        </button>
                    </div>

                    <!-- SECCIÓN 3: RESPALDOS DISPONIBLES EN EL SERVIDOR -->
                    <div class="form-col-12 border-b pb-3 mb-3 mt-6">
                        <div class="flex items-center justify-between">
                            <h4 class="font-client font-medium text-base">{{ $t('label.backup_history') }}</h4>
                            <button type="button" @click="fetchServerBackups" class="db-btn py-1 px-3 border hover:bg-gray-100 text-xs">
                                <i class="fa-solid fa-arrows-rotate mr-1"></i>
                                <span>{{ $t('button.refresh') }}</span>
                            </button>
                        </div>
                    </div>

                    <div class="form-col-12 mb-4">
                        <div v-if="serverBackups.length === 0" class="flex flex-col items-center justify-center py-10 border rounded-md text-gray-500 text-sm">
                            <i class="fa-solid fa-folder-open text-3xl text-gray-300 mb-2 block"></i>
                            <span>{{ $t('label.no_backups_found') }}</span>
                        </div>

                        <div v-else class="db-table-responsive overflow-x-auto">
                            <table class="table w-full text-left text-xs border-collapse">
                                <thead>
                                    <tr class="bg-gray-50 text-gray-600 border-b">
                                        <th class="p-3 font-semibold">{{ $t('label.file_name') }}</th>
                                        <th class="p-3 font-semibold">{{ $t('label.created_at') }}</th>
                                        <th class="p-3 font-semibold">{{ $t('label.file_size') }}</th>
                                        <th class="p-3 font-semibold text-right">{{ $t('label.action') }}</th>
                                    </tr>
                                </thead>
                                <tbody class="divide-y divide-gray-100">
                                    <tr v-for="b in serverBackups" :key="b.filename" class="hover:bg-gray-50/50 transition">
                                        <td class="p-3 font-mono font-medium text-gray-800">
                                            <i class="fa-regular fa-file-zipper text-primary mr-1.5"></i>
                                            {{ b.filename }}
                                        </td>
                                        <td class="p-3 text-gray-600">{{ b.created_at_human }}</td>
                                        <td class="p-3 font-mono text-gray-600">{{ b.formatted_size }}</td>
                                        <td class="p-3 text-right">
                                            <div class="flex items-center justify-end gap-2">
                                                <!-- Botón Descargar -->
                                                <button
                                                    type="button"
                                                    @click="downloadServerBackup(b.filename)"
                                                    class="db-btn py-1 px-2.5 text-xs text-primary bg-primary/10 hover:bg-primary/20 rounded-md font-medium flex items-center gap-1"
                                                    :title="$t('button.download')"
                                                >
                                                    <i class="fa-solid fa-download"></i>
                                                    <span>{{ $t('button.download') }}</span>
                                                </button>

                                                <!-- Botón Importar Directo -->
                                                <button
                                                    type="button"
                                                    @click="quickImportServerBackup(b.filename)"
                                                    class="db-btn py-1 px-2.5 text-xs text-amber-700 bg-amber-100 hover:bg-amber-200 rounded-md font-medium flex items-center gap-1"
                                                    :title="$t('button.start_import')"
                                                >
                                                    <i class="fa-solid fa-file-import"></i>
                                                    <span>{{ $t('button.start_import') }}</span>
                                                </button>

                                                <!-- Botón Eliminar -->
                                                <button
                                                    type="button"
                                                    @click="deleteServerBackup(b.filename)"
                                                    class="db-btn py-1 px-2 text-xs text-red-600 bg-red-50 hover:bg-red-100 rounded-md"
                                                    :title="$t('button.delete')"
                                                >
                                                    <i class="fa-solid fa-trash-can"></i>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
            <template v-else>

            <div class="w-full pt-1">
                <div class="flex justify-start items-center gap-2">
                    <button type="button" class="db-btn py-1.5 border border hover:bg-gray-100 text-sm" @click="showHistoryModal = true">
                        <i class="fa-solid fa-layer-group"></i>
                        <span>{{ $t('button.view_execution_history') }}</span>
                    </button>
                    <button
                        type="button"
                        class="db-btn py-1.5 border border hover:bg-gray-100 text-sm"
                        :class="{ 'disabled:opacity-50 disabled:bg-gray': isResettingBrowserCache }"
                        :disabled="isAnyActionProcessing"
                        @click="resetBrowserCache"
                    >
                        <template v-if="isResettingBrowserCache">
                            <svg class="inline-block mr-1 animate-spin" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                                <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="3" opacity="0.25"></circle>
                                <path d="M22 12a10 10 0 0 1-10 10" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"></path>
                            </svg>
                            <span>{{ $t('button.processing') }}</span>
                        </template>
                        <template v-else>
                            <i class="fa-solid fa-arrows-rotate"></i>
                            <span>{{ $t('button.reset_browser_cache') }}</span>
                        </template>
                    </button>
                </div>
            </div>

            <div class="border-t pt-6 mt-6">
                <div class="flex flex-wrap items-center justify-between gap-3 mb-3">
                    <h4 class="font-client font-medium text-base">{{ $t('label.queue_jobs_status') }}</h4>
                    <div class="flex items-center justify-end gap-2">
                      <span v-if="queueStateClass !== 'bg-success'" class="d-blok w-4 h-4 rounded-full ml-2" :class="queueStateClass"></span>
                      <button
                          type="button"
                          class="db-btn p-2 border hover:bg-gray-100 text-sm"
                          :class="{ 'disabled:opacity-50 disabled:bg-gray': isRefreshingQueueState }"
                          @click="refreshQueueState"
                      >
                          <template v-if="isRefreshingQueueState">
                              <svg class="inline-block mr-1 animate-spin" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                                  <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="3" opacity="0.25"></circle>
                                  <path d="M22 12a10 10 0 0 1-10 10" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"></path>
                              </svg>
                          </template>
                          <template v-else>
                              <i class="fa-solid fa-arrows-rotate"></i>
                          </template>
                      </button>
                      <button type="button" class="db-btn py-1.5 border hover:bg-gray-100 text-sm"
                          @click="toggleQueuePanel">
                          <span>{{ showQueuePanel ? $t('button.hide_state') : $t('button.view_state') }}&nbsp;</span>
                          <i class="fa-solid fa-chevron-down transition-transform duration-200 config-chevron"
                              :class="{ 'is-open': showQueuePanel }"></i>
                      </button>
                    </div>
                </div>

                <div v-if="showQueuePanel" class="rounded border p-4">
                    <div class="flex flex-wrap items-center gap-3 border-b pb-3 mb-3">
                        <span class="inline-flex items-center gap-2 text-sm font-medium">
                            {{ queueStateLabel }}
                        </span>
                        <span class="text-sm">{{ $t('label.pending_jobs') }}: {{ queueMonitor.pending_jobs }}</span>
                        <span class="text-sm">( {{ $t('label.queue_ai_import') }}: {{ queuePendingAiImport }}</span>
                        <span class="text-sm">{{ $t('label.queue_default') }}: {{ queuePendingDefault }} )</span> | 
                        <span class="text-sm">{{ $t('label.failed_jobs') }}: {{ queueMonitor.failed_jobs }}</span> | 
                        <span class="text-sm">{{ $t('label.active_worker') }}: {{ queueMonitor.worker_active ? $t('label.yes') : $t('label.no') }}</span>
                    </div>

                    <div class="flex flex-wrap gap-2">
                        <button type="button" class="db-btn py-2 text-white bg-warning disabled:opacity-50 disabled:bg-gray text-sm"
                            :class="{ 'disabled:opacity-50 disabled:bg-gray': queueMonitor.state !== 'yellow' || isAnyActionProcessing }"
                            :disabled="queueMonitor.state !== 'yellow' || isAnyActionProcessing" @click="runCommand('queue_process_pending')">
                            <template v-if="isCommandRunning('queue_process_pending')">
                                <svg class="inline-block mr-1 animate-spin" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                                    <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="3" opacity="0.25"></circle>
                                    <path d="M22 12a10 10 0 0 1-10 10" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"></path>
                                </svg>
                                <span>{{ $t('button.processing') }}</span>
                            </template>
                            <template v-else>
                                <i class="fa-solid fa-arrow-rotate-right"></i>
                                <span>{{ $t('button.process_pending_queues') }}</span>
                            </template>
                        </button>

                        <button type="button" class="db-btn py-2 text-white bg-danger disabled:opacity-50 disabled:bg-gray text-sm"
                            :class="{ 'disabled:opacity-50 disabled:bg-gray': queueMonitor.failed_jobs < 1 || isAnyActionProcessing }"
                            :disabled="queueMonitor.failed_jobs < 1 || isAnyActionProcessing" @click="runCommand('queue_failed_retry_all')">
                            <template v-if="isCommandRunning('queue_failed_retry_all')">
                                <svg class="inline-block mr-1 animate-spin" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                                    <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="3" opacity="0.25"></circle>
                                    <path d="M22 12a10 10 0 0 1-10 10" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"></path>
                                </svg>
                                <span>{{ $t('button.processing') }}</span>
                            </template>
                            <template v-else>
                                <i class="fa-solid fa-arrow-rotate-right"></i>
                                <span>{{ $t('button.retry_failed_jobs') }}</span>
                            </template>
                        </button>

                        <button type="button" class="db-btn py-2 text-white bg-secondary disabled:opacity-50 disabled:bg-gray text-sm"
                            :class="{ 'disabled:opacity-50 disabled:bg-gray': queueMonitor.failed_jobs < 1 || isAnyActionProcessing }"
                            :disabled="queueMonitor.failed_jobs < 1 || isAnyActionProcessing" @click="runCommand('queue_failed_clear')">
                            <template v-if="isCommandRunning('queue_failed_clear')">
                                <svg class="inline-block mr-1 animate-spin" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                                    <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="3" opacity="0.25"></circle>
                                    <path d="M22 12a10 10 0 0 1-10 10" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"></path>
                                </svg>
                                <span>{{ $t('button.processing') }}</span>
                            </template>
                            <template v-else>
                                <i class="fa-solid fa-broom"></i>
                                <span>{{ $t('button.clear_failed_jobs') }}</span>
                            </template>
                        </button>
                    </div>
                </div>
            </div>

            <div class="border-t pt-6 mt-6">
                <div class="flex flex-wrap items-center justify-between gap-3 mb-3">
                    <h4 class="font-client font-medium text-base">{{ $t('label.maintenance_commands') }}</h4>
                    <div class="flex items-center gap-3">
                        <button type="button" class="db-btn py-1.5 border border hover:bg-gray-100 text-sm" @click="showCommandPanel = !showCommandPanel">
                            <span>{{ showCommandPanel ? $t('button.hide_commands') : $t('button.view_commands') }}&nbsp;</span>
                            <i class="fa-solid fa-chevron-down transition-transform duration-200 config-chevron"
                                :class="{ 'is-open': showCommandPanel }"></i>
                        </button>
                    </div>
                </div>
                <div v-if="showCommandPanel" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 rounded border p-4">
                    <button v-for="cmd in commandButtons" :key="cmd.key" type="button"
                        :disabled="isAnyActionProcessing"
                        @click="runCommand(cmd.key)" class="db-btn py-2 border bg-white hover:bg-gray-100 text-sm flex justify-start items-center gap-1">
                        <template v-if="isCommandRunning(cmd.key)">
                            <svg class="inline-block mr-1 animate-spin" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                                <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="3" opacity="0.25"></circle>
                                <path d="M22 12a10 10 0 0 1-10 10" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"></path>
                            </svg>
                            <span>{{ $t('button.processing') }}</span>
                        </template>
                        <template v-else>
                            <i class="fa-solid fa-terminal"></i>
                            <span>{{ $t(cmd.labelKey) }}</span>
                        </template>
                        <span class="db-tooltip">{{ $t(cmd.tooltipKey) }}</span>
                    </button>
                </div>
            </div>

            <!-- APARTADO DE COMANDOS DE MIGRACIONES Y BASE DE DATOS -->
            <div class="border-t pt-6 mt-6">
                <div class="flex flex-wrap items-center justify-between gap-3 mb-3">
                    <h4 class="font-client font-medium text-base">{{ $t('label.database_and_migration_commands') }}</h4>
                    <div class="flex items-center gap-3">
                        <button type="button" class="db-btn py-1.5 border hover:bg-gray-100 text-sm" @click="showMigrationPanel = !showMigrationPanel">
                            <span>{{ showMigrationPanel ? $t('button.hide_commands') : $t('button.view_commands') }}&nbsp;</span>
                            <i class="fa-solid fa-chevron-down transition-transform duration-200 config-chevron"
                                :class="{ 'is-open': showMigrationPanel }"></i>
                        </button>
                    </div>
                </div>
                <div v-if="showMigrationPanel" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-3 rounded border p-4">
                    <!-- 1. migrate:status -->
                    <button type="button"
                        :disabled="isAnyActionProcessing"
                        @click="runCommand('migrate_status')" class="db-btn py-2 border bg-white hover:bg-gray-100 text-sm flex justify-start items-center gap-1">
                        <template v-if="isCommandRunning('migrate_status')">
                            <svg class="inline-block mr-1 animate-spin" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                                <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="3" opacity="0.25"></circle>
                                <path d="M22 12a10 10 0 0 1-10 10" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"></path>
                            </svg>
                            <span>{{ $t('button.processing') }}</span>
                        </template>
                        <template v-else>
                            <i class="fa-solid fa-list-check"></i>
                            <span>{{ $t('label.cmd_migrate_status') }}</span>
                        </template>
                        <span class="db-tooltip">{{ $t('label.cmd_tip_migrate_status') }}</span>
                    </button>

                    <!-- 2. migrate -->
                    <button type="button"
                        :disabled="isAnyActionProcessing"
                        @click="runCommand('migrate')" class="db-btn py-2 border bg-white hover:bg-gray-100 text-sm flex justify-start items-center gap-1">
                        <template v-if="isCommandRunning('migrate')">
                            <svg class="inline-block mr-1 animate-spin" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                                <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="3" opacity="0.25"></circle>
                                <path d="M22 12a10 10 0 0 1-10 10" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"></path>
                            </svg>
                            <span>{{ $t('button.processing') }}</span>
                        </template>
                        <template v-else>
                            <i class="fa-solid fa-database"></i>
                            <span>{{ $t('label.cmd_migrate') }}</span>
                        </template>
                        <span class="db-tooltip">{{ $t('label.cmd_tip_migrate') }}</span>
                    </button>

                    <!-- 3. migrate_single (con prompt/modal) -->
                    <button type="button"
                        :disabled="isAnyActionProcessing"
                        @click="openMigrationSingleModal" class="db-btn py-2 border bg-white hover:bg-gray-100 text-sm flex justify-start items-center gap-1">
                        <template v-if="isCommandRunning('migrate_single')">
                            <svg class="inline-block mr-1 animate-spin" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                                <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="3" opacity="0.25"></circle>
                                <path d="M22 12a10 10 0 0 1-10 10" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"></path>
                            </svg>
                            <span>{{ $t('button.processing') }}</span>
                        </template>
                        <template v-else>
                            <i class="fa-solid fa-file-code"></i>
                            <span>{{ $t('label.cmd_migrate_single') }}</span>
                        </template>
                        <span class="db-tooltip">{{ $t('label.cmd_tip_migrate_single') }}</span>
                    </button>

                    <!-- 4. db_seed_single (con prompt/modal) -->
                    <button type="button"
                        :disabled="isAnyActionProcessing"
                        @click="openDbSeedSingleModal" class="db-btn py-2 border bg-white hover:bg-gray-100 text-sm flex justify-start items-center gap-1">
                        <template v-if="isCommandRunning('db_seed_single')">
                            <svg class="inline-block mr-1 animate-spin" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                                <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="3" opacity="0.25"></circle>
                                <path d="M22 12a10 10 0 0 1-10 10" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"></path>
                            </svg>
                            <span>{{ $t('button.processing') }}</span>
                        </template>
                        <template v-else>
                            <i class="fa-solid fa-seedling"></i>
                            <span>{{ $t('label.cmd_db_seed_single') }}</span>
                        </template>
                        <span class="db-tooltip">{{ $t('label.cmd_tip_db_seed_single') }}</span>
                    </button>
                </div>
            </div>

            <div class="border-t pt-6 mt-6" v-if="lastResult.command && showLastResultPanel">
                <div class="flex flex-wrap items-center justify-between gap-3 mb-3">
                    <h4 class="font-client font-medium text-base">{{ $t('label.command_result') }}</h4>
                    <button
                        type="button"
                        class="db-btn py-1.5 border border hover:bg-gray-100 text-sm"
                        @click="hideLastResultPanel"
                    >
                        <i class="fa-solid fa-eye-slash"></i>
                        <span>{{ $t('button.hide_results') }}</span>
                    </button>
                </div>
                <div class="rounded border p-4 bg-gray-100">
                    <p class="text-sm"><strong>{{ $t('label.command') }}:</strong> {{ lastResult.command }}</p>
                    <p class="text-sm mb-2"><strong>{{ $t('label.status') }}:</strong> {{ getLocalizedStatus(lastResult.status) }}</p>
                    <pre class="text-xs whitespace-pre-wrap terminal-look">{{ lastResult.output || $t('label.no_output') }}</pre>
                </div>
            </div>

            <div class="border-t pt-6 mt-6">
                <div class="flex flex-wrap items-center justify-between gap-3">
                    <h4 class="font-client font-medium text-base">{{ $t('label.verification_checklist') }}</h4>
                    <div class="flex items-center gap-2">
                        <button
                            type="button"
                            class="db-btn p-2 border border hover:bg-gray-100 text-sm"
                            @click="clearChecklistState"
                            :title="$t('button.clear_checklist')"
                        >
                            <i class="fa-solid fa-arrows-rotate"></i>
                        </button>
                        <button type="button" class="db-btn py-1.5 border border hover:bg-gray-100 gap-1 text-sm" @click="showChecklistPanel = !showChecklistPanel">
                            <span>{{ showChecklistPanel ? $t('button.hide_checklist') : $t('button.view_checklist') }}&nbsp;</span>
                            <i class="fa-solid fa-chevron-down transition-transform duration-200 config-chevron"
                                :class="{ 'is-open': showChecklistPanel }"></i>
                        </button>
                    </div>
                </div>
                <div v-if="showChecklistPanel" class="grid grid-cols-1 md:grid-cols-2 gap-2 border rounded-lg p-4 mt-3">
                    <label v-for="item in checklist" :key="item.key" class="flex items-center gap-2">
                        <input type="checkbox" v-model="item.done" class="cs-custom-checkbox" />
                        <span class="text-sm">{{ $t(item.labelKey) }}</span>
                    </label>
                </div>
            </div>
            </template>

            <div v-if="showHistoryModal" class="fixed inset-0 z-50 flex items-center justify-center bg-light/50 margin-sidebar px-4" @click.self="showHistoryModal = false">
                <div class="w-full max-w-5xl rounded-lg bg-white shadow-md border max-h-[85vh] overflow-hidden">
                    <div class="flex items-center justify-between border-b px-4 pt-4 pb-3">
                        <h4 class="font-medium font-client text-base">{{ $t('label.execution_history') }}</h4>
                        <button type="button" class="text-slate-500 hover:text-slate-700" @click="showHistoryModal = false">
                            <i class="fa-solid fa-xmark text-lg"></i>
                        </button>
                    </div>
                    <div class="p-4 overflow-auto max-h-[70vh]">
                        <div v-if="commandHistory.length === 0" class="text-sm">
                            {{ $t('label.no_execution_history') }}
                        </div>
                        <table v-else class="db-table w-full">
                            <thead class="db-table-head no-border-top">
                                <tr class="db-table-head-tr">
                                    <th class="db-table-head-th">{{ $t('label.date') }}</th>
                                    <th class="db-table-head-th">{{ $t('label.command') }}</th>
                                    <th class="db-table-head-th">{{ $t('label.status') }}</th>
                                    <th class="db-table-head-th">{{ $t('label.output') }}</th>
                                </tr>
                            </thead>
                            <tbody class="db-table-body">
                                <tr v-for="(entry, index) in commandHistory" :key="index" class="db-table-body-tr">
                                    <td class="db-table-body-td">{{ entry.executed_at || '-' }}</td>
                                    <td class="db-table-body-td">{{ entry.command || '-' }}</td>
                                    <td class="db-table-body-td">{{ getLocalizedStatus(entry.status) }}</td>
                                    <td class="db-table-body-td">
                                        <span class="block wrap" :title="entry.output || ''">
                                            {{ entry.output || $t('label.no_output') }}
                                        </span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <!-- Modal para Migración Individual -->
            <div v-if="showMigrationSingleModal" class="fixed inset-0 z-50 flex items-center justify-center bg-light/50 margin-sidebar px-4" @click.self="showMigrationSingleModal = false">
                <div class="w-full max-w-md rounded-lg bg-white shadow-lg">
                    <div class="flex items-center justify-between border-b px-4 pt-4 pb-3">
                        <h4 class="font-medium font-client text-base">{{ $t('label.cmd_migrate_single') }}</h4>
                        <button type="button" class="text-slate-500 hover:text-slate-700" @click="showMigrationSingleModal = false">
                            <i class="fa-solid fa-xmark text-lg"></i>
                        </button>
                    </div>
                    <form @submit.prevent="submitMigrationSingle" class="w-full p-4">
                        <p class="text-xs text-slate-500 mb-3">{{ $t('label.enter_migration_path') }}</p>
                        <div class="mb-3" v-if="availableMigrations.length > 0">
                            <label class="db-field-title" for="migration_select">{{ $t('label.select_migration') }}</label>
                            <vue-select
                                class="db-field-control f-b-custom-select"
                                id="migration_select"
                                v-model="selectedMigrationOption"
                                :options="availableMigrations"
                                label-by="label"
                                value-by="value"
                                :close-on-select="true"
                                :searchable="true"
                                :clear-on-close="true"
                                :placeholder="$t('label.select_migration')"
                                @update:modelValue="onMigrationSelected"
                            />
                        </div>
                        <div class="mb-4">
                            <label class="db-field-title required" for="migration_path_input">{{ $t('label.migration_path') }}</label>
                            <input
                                id="migration_path_input"
                                type="text"
                                v-model="migrationSinglePath"
                                :placeholder="$t('label.migration_path_placeholder')"
                                class="db-field-control text-sm w-full"
                                required
                            />
                        </div>
                        <div class="flex justify-end gap-2">
                            <button type="button" class="db-btn py-1.5 px-3 border hover:bg-gray-100 text-sm" @click="showMigrationSingleModal = false">
                                {{ $t('button.cancel') }}
                            </button>
                            <button type="submit" :disabled="!migrationSinglePath.trim() || isAnyActionProcessing" class="db-btn py-1.5 px-3 bg-primary text-white text-sm">
                                {{ $t('label.execute') }}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            <!-- Modal para Seeder Individual -->
            <div v-if="showDbSeedSingleModal" class="fixed inset-0 z-50 flex items-center justify-center bg-light/50 margin-sidebar px-4" @click.self="showDbSeedSingleModal = false">
                <div class="w-full max-w-md rounded-lg bg-white shadow-lg">
                    <div class="flex items-center justify-between border-b px-4 pt-4 pb-3">
                        <h4 class="font-medium font-client text-base">{{ $t('label.cmd_db_seed_single') }}</h4>
                        <button type="button" class="text-slate-500 hover:text-slate-700" @click="showDbSeedSingleModal = false">
                            <i class="fa-solid fa-xmark text-lg"></i>
                        </button>
                    </div>
                    <form @submit.prevent="submitDbSeedSingle" class="p-4">
                        <p class="text-xs text-slate-500 mb-3">{{ $t('label.enter_seeder_class') }}</p>
                        <div class="mb-3" v-if="availableSeeders.length > 0">
                            <label class="db-field-title" for="seeder_select">{{ $t('label.select_seeder') }}</label>
                            <vue-select
                                class="db-field-control f-b-custom-select"
                                id="seeder_select"
                                v-model="selectedSeederOption"
                                :options="availableSeeders"
                                label-by="label"
                                value-by="value"
                                :close-on-select="true"
                                :searchable="true"
                                :clear-on-close="true"
                                :placeholder="$t('label.select_seeder')"
                                @update:modelValue="onSeederSelected"
                            />
                        </div>
                        <div class="mb-4">
                            <label class="db-field-title required" for="seeder_class_input">{{ $t('label.seeder_class') }}</label>
                            <input
                                id="seeder_class_input"
                                type="text"
                                v-model="dbSeedSingleClass"
                                :placeholder="$t('label.seeder_class_placeholder')"
                                class="db-field-control text-sm w-full"
                                required
                            />
                        </div>
                        <div class="flex justify-end gap-2">
                            <button type="button" class="db-btn py-1.5 px-3 border hover:bg-gray-100 text-sm" @click="showDbSeedSingleModal = false">
                                {{ $t('button.cancel') }}
                            </button>
                            <button type="submit" :disabled="!dbSeedSingleClass.trim() || isAnyActionProcessing" class="db-btn py-1.5 px-3 bg-primary text-white text-sm">
                                {{ $t('label.execute') }}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import LoadingComponent from '../../components/LoadingComponent';
import alertService from '../../../../services/alertService';

const CHECKLIST_STORAGE_KEY = 'system-tools-checklist-state';
const CHECKLIST_STORAGE_TTL_MS = 12 * 60 * 60 * 1000;

export default {
    name: 'SystemComponent',
    components: { LoadingComponent },
    data() {
        return {
            loading: {
                isActive: false,
            },
            isSaving: false,
            onUpdateActive: false,
            isResettingBrowserCache: false,
            isRefreshingQueueState: false,
            runningCommand: '',
            backgroundRunningCommand: '',
            pollingIntervalId: null,
            showQueuePanel: false,
            queuePanelManuallyToggled: false,
            showCommandPanel: false,
            showMigrationPanel: false,
            showChecklistPanel: false,
            showHistoryModal: false,
            showMigrationSingleModal: false,
            showDbSeedSingleModal: false,
            migrationSinglePath: '',
            dbSeedSingleClass: '',
            availableMigrations: [],
            availableSeeders: [],
            selectedMigrationOption: null,
            selectedSeederOption: null,
            activeSystemTab: 'operations',
            showLastResultPanel: true,
            isExporting: false,
            isImporting: false,
            selectedBackupFile: null,
            importMode: 'upload',
            selectedServerBackup: '',
            serverBackups: [],
            form: {
                system_queue_enabled: 1,
                system_app_env: 'production',
                system_app_debug: 0,
                system_demo: 0,
                system_auto_login: 0,
                system_login_mode: 'normal',
                system_demo_giveaway: 0,
                system_queue_connection: 'database',
                system_db_connection: 'mysql',
                system_db_host: '',
                system_db_port: '3306',
                system_db_database: '',
                system_db_username: '',
                system_db_password: '',
            },
            initialForm: {
                system_queue_enabled: 1,
                system_app_env: 'production',
                system_app_debug: 0,
                system_demo: 0,
                system_auto_login: 0,
                system_login_mode: 'normal',
                system_demo_giveaway: 0,
                system_queue_connection: 'database',
                system_db_connection: 'mysql',
                system_db_host: '',
                system_db_port: '3306',
                system_db_database: '',
                system_db_username: '',
                system_db_password: '',
            },
            queueMonitor: {
                state: 'green',
                pending_jobs: 0,
                failed_jobs: 0,
                worker_active: false,
            },
            queueConnectionOptions: ['sync', 'database', 'redis', 'sqs'],
            dbConnectionOptions: ['mysql'],
            commandButtons: [
                { key: 'queue_work', labelKey: 'label.cmd_queue_work_by_env', tooltipKey: 'label.cmd_tip_queue_work_by_env' },
                { key: 'queue_restart', labelKey: 'label.cmd_queue_restart', tooltipKey: 'label.cmd_tip_queue_restart' },
                { key: 'db_test', labelKey: 'label.cmd_db_test', tooltipKey: 'label.cmd_tip_db_test' },
                { key: 'optimize_clear', labelKey: 'label.cmd_optimize_clear', tooltipKey: 'label.cmd_tip_optimize_clear' },
                { key: 'optimize', labelKey: 'label.cmd_optimize', tooltipKey: 'label.cmd_tip_optimize' },
                { key: 'cache_clear', labelKey: 'label.cmd_cache_clear', tooltipKey: 'label.cmd_tip_cache_clear' },
                { key: 'config_clear', labelKey: 'label.cmd_config_clear', tooltipKey: 'label.cmd_tip_config_clear' },
                { key: 'config_cache', labelKey: 'label.cmd_config_cache', tooltipKey: 'label.cmd_tip_config_cache' },
                { key: 'route_clear', labelKey: 'label.cmd_route_clear', tooltipKey: 'label.cmd_tip_route_clear' },
                { key: 'route_cache', labelKey: 'label.cmd_route_cache', tooltipKey: 'label.cmd_tip_route_cache' },
                { key: 'view_clear', labelKey: 'label.cmd_view_clear', tooltipKey: 'label.cmd_tip_view_clear' },
                { key: 'view_cache', labelKey: 'label.cmd_view_cache', tooltipKey: 'label.cmd_tip_view_cache' },
                { key: 'storage_link', labelKey: 'label.cmd_storage_link', tooltipKey: 'label.cmd_tip_storage_link' },
            ],
            checklist: [
                { key: 'save_settings', labelKey: 'label.checklist_save_settings', done: false },
                { key: 'run_queue', labelKey: 'label.checklist_run_queue', done: false },
                { key: 'db_test', labelKey: 'label.checklist_db_test', done: false },
                { key: 'clear_cache', labelKey: 'label.checklist_clear_cache', done: false },
                { key: 'cache_rebuild', labelKey: 'label.checklist_cache_rebuild', done: false },
                { key: 'storage_link', labelKey: 'label.checklist_storage_link', done: false },
            ],
            lastResult: {
                command: '',
                status: '',
                output: '',
            },
            commandHistory: [],
            errors: {},
        };
    },
    computed: {
        showDbNetworkFields: function () {
            return this.form.system_db_connection !== 'sqlite';
        },
        showDbAuthFields: function () {
            return this.form.system_db_connection !== 'sqlite';
        },
        queueStateClass: function () {
            if (this.queueMonitor.state === 'red') {
                return 'bg-danger';
            }

            if (this.queueMonitor.state === 'yellow') {
                return 'bg-warning';
            }

            return 'bg-success';
        },
        queueStateLabel: function () {
            if (this.queueMonitor.state === 'red') {
                return this.$t('label.queue_state_red');
            }

            if (this.queueMonitor.state === 'yellow') {
                return this.$t('label.queue_state_yellow');
            }

            return this.$t('label.queue_state_green');
        },
        pendingByQueue: function () {
            const rows = this.queueMonitor?.pending_by_queue;
            return rows && typeof rows === 'object' ? rows : {};
        },
        queuePendingAiImport: function () {
            return Number(this.pendingByQueue['ai-import'] || 0);
        },
        queuePendingDefault: function () {
            return Number(this.pendingByQueue.default || 0);
        },
        isAnyCommandRunning: function () {
            return this.runningCommand !== '' || this.backgroundRunningCommand !== '';
        },
        isAnyActionProcessing: function () {
            return this.isSaving || this.isResettingBrowserCache || this.isAnyCommandRunning || this.backgroundRunningCommand !== '';
        },
        hasQueueIncidence: function () {
            return Number(this.queueMonitor?.pending_jobs || 0) > 0 || Number(this.queueMonitor?.failed_jobs || 0) > 0;
        },
    },
    mounted() {
        this.loadChecklistState();
        this.list();
    },
    beforeUnmount() {
        if (this.pollingIntervalId) {
            window.clearInterval(this.pollingIntervalId);
        }
    },
    watch: {
        checklist: {
            deep: true,
            handler: function () {
                this.persistChecklistState();
            },
        },
        hasQueueIncidence: {
            immediate: true,
            handler: function (hasIncidence) {
                if (hasIncidence) {
                    this.showQueuePanel = true;
                    return;
                }

                if (!this.queuePanelManuallyToggled) {
                    this.showQueuePanel = false;
                }
            },
        },
        'form.system_demo': function (newVal) {
            if (Number(newVal) === 0) {
                this.form.system_auto_login = 0;
                this.form.system_login_mode = 'normal';
                this.form.system_demo_giveaway = 0;
            }
        },
        'form.system_auto_login': function (newVal) {
            if (Number(newVal) === 0) {
                this.form.system_login_mode = 'normal';
                this.form.system_demo_giveaway = 0;
            }
        },
        'form.system_db_connection': function (newConnection, oldConnection) {
            if (!newConnection || newConnection === oldConnection) {
                return;
            }

            if (newConnection === 'sqlite') {
                this.form.system_db_host = '';
                this.form.system_db_port = '';
                this.form.system_db_username = '';
                this.form.system_db_password = '';
                return;
            }

            if (!this.form.system_db_port) {
                const defaultPorts = {
                    mysql: '3306',
                    pgsql: '5432',
                    sqlsrv: '1433',
                };

                this.form.system_db_port = defaultPorts[newConnection] || this.form.system_db_port;
            }
        },
    },
    methods: {
        list: function (withOverlay = true) {
            if (withOverlay) {
                this.loading.isActive = true;
            }

            return this.$store.dispatch('systemTools/lists').then((res) => {
                const data = res.data.data;
                this.form.system_queue_enabled = data.system_queue_enabled ? 1 : 0;
                this.form.system_app_env = data.system_app_env;
                this.form.system_app_debug = data.system_app_debug ? 1 : 0;
                this.form.system_demo = data.system_demo ? 1 : 0;
                this.form.system_auto_login = data.system_demo ? (data.system_auto_login ? 1 : 0) : 0;
                this.form.system_login_mode = (data.system_demo && data.system_auto_login && data.system_login_mode === 'auto') ? 'auto' : 'normal';
                this.form.system_demo_giveaway = (data.system_demo && data.system_auto_login && data.system_demo_giveaway) ? 1 : 0;
                this.form.system_queue_connection = data.system_queue_connection;
                this.form.system_db_connection = data.system_db_connection || 'mysql';
                this.form.system_db_host = data.system_db_host;
                this.form.system_db_port = data.system_db_port;
                this.form.system_db_database = data.system_db_database;
                this.form.system_db_username = data.system_db_username;
                this.form.system_db_password = data.system_db_password;
                this.initialForm = { ...this.form };
                this.queueConnectionOptions = data.available_queue_connections || this.queueConnectionOptions;
                this.dbConnectionOptions = data.available_db_connections || this.dbConnectionOptions;
                this.availableMigrations = data.available_migrations || [];
                this.availableSeeders = data.available_seeders || [];
                this.queueMonitor = data.queue_monitor || this.queueMonitor;
                this.syncQueuePanelVisibility();
                this.commandHistory = data.command_history || [];
                if (withOverlay) {
                    this.loading.isActive = false;
                }
                return res;
            }).catch((err) => {
                if (withOverlay) {
                    this.loading.isActive = false;
                }
                throw err;
            });
        },
        save: function () {
            if (this.isAnyActionProcessing) {
                return;
            }

            if (this.form.system_app_env !== this.initialForm.system_app_env) {
                const okEnv = window.confirm(this.$t('message.confirm_change_app_env'));
                if (!okEnv) {
                    return;
                }
            }

            const dbChanged = this.form.system_db_host !== this.initialForm.system_db_host
                || this.form.system_db_port !== this.initialForm.system_db_port
                || this.form.system_db_database !== this.initialForm.system_db_database
                || this.form.system_db_username !== this.initialForm.system_db_username
                || this.form.system_db_password !== this.initialForm.system_db_password
                || this.form.system_db_connection !== this.initialForm.system_db_connection;

            if (dbChanged) {
                const okDb = window.confirm(this.$t('message.confirm_change_db_config'));
                if (!okDb) {
                    return;
                }
            }

            this.isSaving = true;
            this.onUpdateActive = true;
            this.$store.dispatch('systemTools/save', this.form).then(() => {
                this.errors = {};
                this.markChecklistDone('save_settings');
                alertService.successFlip(1, this.$t('label.system_and_maintenance'));
                this.list(false);
            }).catch((err) => {
                this.errors = err.response?.data?.errors || {};
            }).finally(() => {
                this.isSaving = false;
                this.onUpdateActive = false;
            });
        },
        setActiveSystemTab: function (tab) {
            this.activeSystemTab = tab;
            if (tab === 'backup') {
                this.fetchServerBackups();
            }
        },
        toggleQueuePanel: function () {
            this.queuePanelManuallyToggled = true;
            this.showQueuePanel = !this.showQueuePanel;
        },
        syncQueuePanelVisibility: function () {
            if (this.hasQueueIncidence) {
                this.showQueuePanel = true;
                return;
            }

            if (!this.queuePanelManuallyToggled) {
                this.showQueuePanel = false;
            }
        },
        getLocalizedStatus: function (status) {
            return status === 'ok' ? this.$t('label.successful') : this.$t('label.failed_command');
        },
        hideLastResultPanel: function () {
            this.showLastResultPanel = false;
        },
        getChecklistSnapshot: function () {
            return this.checklist.reduce((accumulator, item) => {
                accumulator[item.key] = Boolean(item.done);
                return accumulator;
            }, {});
        },
        persistChecklistState: function () {
            try {
                const payload = {
                    expiresAt: Date.now() + CHECKLIST_STORAGE_TTL_MS,
                    items: this.getChecklistSnapshot(),
                };

                window.localStorage.setItem(CHECKLIST_STORAGE_KEY, JSON.stringify(payload));
            } catch (error) {
                // Ignore localStorage access errors in private modes.
            }
        },
        loadChecklistState: function () {
            try {
                const rawValue = window.localStorage.getItem(CHECKLIST_STORAGE_KEY);
                if (!rawValue) {
                    return;
                }

                const parsed = JSON.parse(rawValue);
                const expiresAt = Number(parsed?.expiresAt || 0);
                if (!expiresAt || Date.now() > expiresAt) {
                    window.localStorage.removeItem(CHECKLIST_STORAGE_KEY);
                    return;
                }

                const savedItems = parsed?.items && typeof parsed.items === 'object' ? parsed.items : {};
                this.checklist = this.checklist.map((item) => ({
                    ...item,
                    done: Boolean(savedItems[item.key]),
                }));

                const hasCheckedItems = this.checklist.some((item) => item.done);
                if (hasCheckedItems) {
                    this.showChecklistPanel = true;
                }
            } catch (error) {
                try {
                    window.localStorage.removeItem(CHECKLIST_STORAGE_KEY);
                } catch (e) {
                    // Ignore localStorage access errors in private modes.
                }
            }
        },
        clearChecklistState: function () {
            this.checklist = this.checklist.map((item) => ({
                ...item,
                done: false,
            }));

            try {
                window.localStorage.removeItem(CHECKLIST_STORAGE_KEY);
            } catch (error) {
                // Ignore localStorage access errors in private modes.
            }

            alertService.success(this.$t('message.checklist_reset'));
        },
        markChecklistDone: function (checkKey) {
            const index = this.checklist.findIndex((item) => item.key === checkKey);
            if (index === -1 || this.checklist[index].done) {
                return;
            }

            this.checklist[index].done = true;
        },
        markChecklistFromCommand: function (command) {
            const commandChecklistMap = {
                queue_work: ['run_queue'],
                queue_restart: ['run_queue'],
                queue_process_pending: ['run_queue'],
                queue_failed_retry_all: ['run_queue'],
                queue_failed_clear: ['run_queue'],
                db_test: ['db_test'],
                optimize_clear: ['clear_cache'],
                cache_clear: ['clear_cache'],
                config_clear: ['clear_cache'],
                route_clear: ['clear_cache'],
                view_clear: ['clear_cache'],
                optimize: ['cache_rebuild'],
                config_cache: ['cache_rebuild'],
                route_cache: ['cache_rebuild'],
                view_cache: ['cache_rebuild'],
                storage_link: ['storage_link'],
            };

            const relatedChecks = commandChecklistMap[command] || [];
            relatedChecks.forEach((checkKey) => this.markChecklistDone(checkKey));
        },
        runCommand: function (command, params = {}) {
            if (this.isAnyActionProcessing) {
                return;
            }

            this.runningCommand = command;
            let commandStatus = 'failed';
            const payload = { command, ...params };

            this.$store.dispatch('systemTools/runCommand', payload).then((res) => {
                this.lastResult = res.data.data;
                this.showLastResultPanel = true;
                this.queueMonitor = this.lastResult.queue_monitor || this.queueMonitor;
                this.commandHistory = this.lastResult.command_history || this.commandHistory;
                if (this.lastResult.status === 'ok') {
                    commandStatus = 'ok';
                    this.markChecklistFromCommand(command);
                    alertService.successFlip(1, this.$t('message.command_executed'));

                    if (command === 'queue_process_pending' || command === 'queue_failed_retry_all') {
                        this.startQueueBackgroundPolling(command);
                    }
                } else {
                    alertService.error(this.lastResult.output || this.$t('error.error_running_command'));
                }
            }).catch((err) => {
                alertService.error(err.response?.data?.message || this.$t('error.error_running_command'));
            }).finally(() => {
                this.runningCommand = '';
                if (this.backgroundRunningCommand === '') {
                    this.list(false);
                }
                this.notifyAiImportStatusRefresh(command, commandStatus);
            });
        },
        openMigrationSingleModal: function () {
            this.selectedMigrationOption = null;
            this.showMigrationSingleModal = true;
        },
        onMigrationSelected: function (val) {
            if (val) {
                this.migrationSinglePath = typeof val === 'object' ? (val.value || '') : val;
            }
        },
        submitMigrationSingle: function () {
            const path = this.migrationSinglePath.trim();
            if (!path) {
                return;
            }
            this.showMigrationSingleModal = false;
            this.runCommand('migrate_single', { path });
        },
        openDbSeedSingleModal: function () {
            this.selectedSeederOption = null;
            this.showDbSeedSingleModal = true;
        },
        onSeederSelected: function (val) {
            if (val) {
                this.dbSeedSingleClass = typeof val === 'object' ? (val.value || '') : val;
            }
        },
        submitDbSeedSingle: function () {
            const className = this.dbSeedSingleClass.trim();
            if (!className) {
                return;
            }
            this.showDbSeedSingleModal = false;
            this.runCommand('db_seed_single', { class: className });
        },
        refreshQueueState: function () {
            if (this.isAnyActionProcessing) {
                return;
            }

            this.isRefreshingQueueState = true;
            this.list(false);
            window.setTimeout(() => {
                this.isRefreshingQueueState = false;
            }, 350);
        },
        startQueueBackgroundPolling: function (command) {
            this.stopQueueBackgroundPolling();

            this.backgroundRunningCommand = command;
            
            const pollInterval = 2500;
            const maxPollTime = 90000; 
            const startTime = Date.now();

            this.pollingIntervalId = window.setInterval(() => {
                if (Date.now() - startTime > maxPollTime) {
                    this.stopQueueBackgroundPolling();
                    return;
                }

                this.list(false).then(() => {
                    if (Number(this.queueMonitor.pending_jobs) === 0 && this.queueMonitor.state === 'green') {
                        this.stopQueueBackgroundPolling();
                    }
                }).catch(() => {
                    this.stopQueueBackgroundPolling();
                });
            }, pollInterval);
        },
        stopQueueBackgroundPolling: function () {
            if (this.pollingIntervalId) {
                window.clearInterval(this.pollingIntervalId);
                this.pollingIntervalId = null;
            }
            this.backgroundRunningCommand = '';
            this.list(false);
        },
        notifyAiImportStatusRefresh: function (command, status) {
            const refreshCommands = ['queue_process_pending', 'queue_failed_retry_all', 'queue_failed_clear'];
            if (!refreshCommands.includes(command)) {
                return;
            }

            const detail = {
                command,
                status,
                timestamp: Date.now(),
            };

            try {
                window.localStorage.setItem('ai-import-status-refresh', JSON.stringify(detail));
            } catch (e) {
                // Ignore localStorage access errors in private modes.
            }

            window.dispatchEvent(new CustomEvent('ai-import:status-refresh', { detail }));
        },
        isCommandRunning: function (command) {
            return this.runningCommand === command || this.backgroundRunningCommand === command;
        },
        resetBrowserCache: async function () {
            if (this.isAnyActionProcessing) {
                return;
            }

            const isBrowserSupported = typeof window !== 'undefined'
                && typeof navigator !== 'undefined'
                && 'serviceWorker' in navigator
                && 'caches' in window;

            if (!isBrowserSupported) {
                alertService.error(this.$t('message.browser_cache_reset_not_supported'));
                return;
            }

            const ok = window.confirm(this.$t('message.confirm_reset_browser_cache'));
            if (!ok) {
                return;
            }

            this.isResettingBrowserCache = true;
            try {
                const registrations = await navigator.serviceWorker.getRegistrations();
                await Promise.all(registrations.map((registration) => registration.unregister()));

                const cacheKeys = await caches.keys();
                await Promise.all(cacheKeys.map((cacheKey) => caches.delete(cacheKey)));

                alertService.success(this.$t('message.browser_cache_reset_done'));

                window.setTimeout(() => {
                    window.location.reload();
                }, 350);
            } catch (error) {
                alertService.error(this.$t('error.error_resetting_browser_cache'));
            } finally {
                this.isResettingBrowserCache = false;
            }
        },
        fetchServerBackups: function () {
            axios.get('/api/admin/setting/backup').then((res) => {
                this.serverBackups = res.data.data || [];
            }).catch((err) => {
                console.error("Error loading backups:", err);
            });
        },
        exportBackup: function () {
            this.isExporting = true;
            axios.post('/api/admin/setting/backup/export').then((response) => {
                this.isExporting = false;
                alertService.success(response.data.message || this.$t('label.generate_backup'));
                this.fetchServerBackups();
            }).catch((error) => {
                this.isExporting = false;
                alertService.error(error.response?.data?.message || this.$t('error.something_wrong'));
            });
        },
        downloadServerBackup: function (filename) {
            window.location.href = `/api/admin/setting/backup/download/${encodeURIComponent(filename)}`;
        },
        deleteServerBackup: function (filename) {
            if (!window.confirm(this.$t('label.confirm_delete_backup'))) {
                return;
            }
            axios.delete(`/api/admin/setting/backup/delete/${encodeURIComponent(filename)}`).then((res) => {
                alertService.success(res.data.message || 'Respaldo eliminado.');
                this.fetchServerBackups();
            }).catch((err) => {
                alertService.error(err.response?.data?.message || this.$t('error.something_wrong'));
            });
        },
        quickImportServerBackup: function (filename) {
            this.importMode = 'server';
            this.selectedServerBackup = filename;
            this.confirmImportBackup();
        },
        handleFileSelect: function (event) {
            this.selectedBackupFile = event.target.files[0] || null;
        },
        confirmImportBackup: function () {
            if (this.importMode === 'upload' && !this.selectedBackupFile) return;
            if (this.importMode === 'server' && !this.selectedServerBackup) return;

            if (window.confirm(this.$t('message.confirm_backup_import'))) {
                this.importBackup();
            }
        },
        importBackup: function () {
            this.isImporting = true;
            this.loading.isActive = true;

            const formData = new FormData();
            if (this.importMode === 'upload') {
                formData.append('file', this.selectedBackupFile);
            } else {
                formData.append('filename', this.selectedServerBackup);
            }

            axios.post('/api/admin/setting/backup/import', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            }).then((response) => {
                this.isImporting = false;
                this.loading.isActive = false;
                alertService.success(this.$t('message.backup_import_success'));
                setTimeout(() => {
                    window.location.reload();
                }, 1500);
            }).catch((error) => {
                this.isImporting = false;
                this.loading.isActive = false;
                alertService.error(error.response?.data?.message || this.$t('error.something_wrong'));
            });
        },
    },
};
</script>

<style scoped>
.config-chevron {
    transition: transform 0.2s ease;
}
.config-chevron.is-open {
    transform: rotate(180deg);
}
</style>